/**
 * EventPresentation — Fundraising Suite, donor management component E.
 *
 * Attach a pitch/impact deck to a donor event and present it full-screen.
 * Reuses the LMS slidesEmbed helper (Canva / Google Slides / Office / PDF /
 * video) so decks render exactly like course slides. Materials persist on the
 * event via PUT /api/donor-events/events/{id}/materials (calendar_events.materials).
 */

import React, { useState } from 'react';
import { ExternalLink, Play, Plus, Presentation, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EventMaterial, setDonorEventMaterials } from '@/services/api/donorEvents';
import { slidesEmbedSrc } from '@/src/app/workforce/courses/slidesEmbed';

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: 'google_slides', label: 'Google Slides' },
  { value: 'canva', label: 'Canva' },
  { value: 'office', label: 'PowerPoint / Office' },
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Video' },
  { value: 'link', label: 'Link' },
];

function Presenter({ material, onClose }: { material: EventMaterial; onClose: () => void }) {
  const src = slidesEmbedSrc(material.kind, material.url);
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-2 text-white">
        <span className="text-sm font-medium">{material.title}</span>
        <button onClick={onClose} className="rounded p-1 hover:bg-white/10" aria-label="Exit presentation">
          <X size={20} />
        </button>
      </div>
      <iframe
        title={material.title}
        src={src}
        className="w-full flex-1 border-0"
        allow="fullscreen; autoplay"
        allowFullScreen
      />
    </div>
  );
}

export function EventPresentation({
  eventId,
  materials,
  onChange,
}: {
  eventId: number;
  materials: EventMaterial[];
  onChange: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState('google_slides');
  const [saving, setSaving] = useState(false);
  const [presenting, setPresenting] = useState<EventMaterial | null>(null);

  const persist = async (next: EventMaterial[]) => {
    setSaving(true);
    try {
      await setDonorEventMaterials(eventId, next);
      onChange();
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!url.trim()) return;
    await persist([...materials, { title: title.trim() || 'Deck', url: url.trim(), kind }]);
    setTitle('');
    setUrl('');
    setKind('google_slides');
    setAdding(false);
  };

  const remove = async (idx: number) => {
    await persist(materials.filter((_, i) => i !== idx));
  };

  return (
    <div className="mb-3 rounded border border-indigo-100 bg-indigo-50/50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1 text-xs font-medium text-gray-600">
          <Presentation size={14} /> Presentation
        </span>
        {!adding ? (
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 text-xs text-indigo-600 hover:underline">
            <Plus size={12} /> Add deck
          </button>
        ) : null}
      </div>

      {materials.length > 0 ? (
        <div className="mb-2 divide-y rounded border border-gray-100 bg-white">
          {materials.map((m, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-1.5">
              <span className="min-w-0 truncate text-sm text-gray-900">{m.title}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPresenting(m)}
                  className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700"
                >
                  <Play size={12} /> Present
                </button>
                <a href={m.url} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-gray-600">
                  <ExternalLink size={13} />
                </a>
                <button onClick={() => remove(i)} className="p-1 text-gray-300 hover:text-red-500">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : !adding ? (
        <p className="text-xs text-gray-400">No deck attached — add a Canva / Slides / PDF link to present.</p>
      ) : null}

      {adding ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Deck title"
              className="w-32 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Share / embed URL"
              className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <Button size="sm" disabled={saving || !url.trim()} onClick={add}>
              Save
            </Button>
            <button onClick={() => setAdding(false)} className="text-xs text-gray-500 hover:underline">
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {presenting ? <Presenter material={presenting} onClose={() => setPresenting(null)} /> : null}
    </div>
  );
}

export default EventPresentation;
